import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatBytes, formatDuration } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { useMediaForRoutine, useDeleteMedia } from '@/data/media';
import { useVideoEnabled } from '@/data/videoStorage';
import { MediaUploader } from '@/components/media/MediaUploader';
import { VideoPlayer } from '@/components/media/VideoPlayer';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';

export const Route = createFileRoute('/admin/competitions/$id/routines/$routineId/media')({
  component: AdminRoutineMedia,
});

function useRoutineContext(routineId: string) {
  return useQuery({
    queryKey: ['competition-routine', routineId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('competition_routines')
        .select('id, name, competition_id, dances:dance_id(id, name)')
        .eq('id', routineId)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as {
        id: string;
        name: string;
        competition_id: string;
        dances: { id: string; name: string } | null;
      } | null;
    },
  });
}

function AdminRoutineMedia() {
  const { id: competitionId, routineId } = Route.useParams();
  const enabled = useVideoEnabled();
  const routine = useRoutineContext(routineId);
  const media = useMediaForRoutine(enabled.data ? routineId : undefined);
  const del = useDeleteMedia();

  if (enabled.isLoading) return <p aria-live="polite">Loading...</p>;

  if (!enabled.data) {
    return (
      <Empty
        title="Video storage is not enabled"
        description="Enable video storage before uploading choreography media."
      />
    );
  }

  return (
    <div className="space-y-10">
      <header>
        <Link
          to="/admin/competitions/$id"
          params={{ id: competitionId }}
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Competition
        </Link>
        <h1 className="mt-2">{routine.data?.name ?? 'Routine'} - media</h1>
        {routine.data?.dances && (
          <p className="mt-1 text-[var(--color-ink-muted)]">
            Linked to dance: {routine.data.dances.name}
          </p>
        )}
      </header>

      <MediaUploader competitionRoutineId={routineId} defaultKind="video" />

      <section aria-labelledby="existing-heading">
        <h2 id="existing-heading" className="text-[var(--text-xl)]">
          Existing media
        </h2>
        {media.isLoading && <p aria-live="polite">Loading...</p>}
        {!media.isLoading && (media.data?.length ?? 0) === 0 && (
          <Empty title="No media yet" description="Upload a video above to get started." />
        )}
        <ul className="mt-4 grid gap-4">
          {(media.data ?? []).map((m) => (
            <li key={m.id} className="surface-elevated grid gap-3 p-4">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <p className="font-medium">{m.title ?? '(untitled)'}</p>
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    <Badge tone="neutral">{m.kind}</Badge>{' '}
                    <Badge
                      tone={
                        m.upload_status === 'complete'
                          ? 'success'
                          : m.upload_status === 'failed'
                            ? 'danger'
                            : 'warn'
                      }
                    >
                      {m.upload_status}
                    </Badge>{' '}
                    {formatBytes(m.size_bytes)}
                    {m.duration_seconds != null && <> · {formatDuration(m.duration_seconds)}</>}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => del.mutate({ id: m.id })}
                  disabled={del.isPending}
                >
                  Delete
                </Button>
              </div>
              {m.upload_status === 'complete' && m.kind === 'video' && (
                <VideoPlayer assetId={m.id} watermark="admin preview" />
              )}
              {m.description && (
                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  {m.description}
                </p>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
